import {
	CIRCLE_SIMPLE_REGEX,
	CIRCLE_WKT_REGEX,
	POLYGON_SIMPLE_REGEX,
	POLYGON_WKT_REGEX,
	WHITESPACE_SPLIT_REGEX,
} from "../constants/fence";
import type { CircleData, FenceType, PolygonData } from "../types/fence";

export { FENCE_TYPE_ICONS, FENCE_TYPE_LABELS } from "../constants/fence";

export function parseCircleFromWKT(area: string): CircleData | null {
	let match = area.match(CIRCLE_WKT_REGEX);

	if (!match) {
		match = area.match(CIRCLE_SIMPLE_REGEX);
	}

	if (!match) {
		return null;
	}

	const lat = Number.parseFloat(match[1]);
	const lng = Number.parseFloat(match[2]);
	const radius = Number.parseFloat(match[3]);

	if (Number.isNaN(lat) || Number.isNaN(lng) || Number.isNaN(radius)) {
		return null;
	}

	return { center: [lat, lng], radius };
}

export function parsePolygonFromWKT(area: string): PolygonData | null {
	let match = area.match(POLYGON_WKT_REGEX);

	if (!match) {
		match = area.match(POLYGON_SIMPLE_REGEX);
	}

	if (!match) {
		return null;
	}

	const coordsStr = match[1];
	const coordPairs = coordsStr
		.split(",")
		.map((pair) => pair.trim().split(WHITESPACE_SPLIT_REGEX));

	const points: [number, number][] = coordPairs.map((pair) => {
		const lat = Number.parseFloat(pair[0]);
		const lng = Number.parseFloat(pair[1]);
		return [lat, lng];
	});

	if (points.some((p) => p.some(Number.isNaN))) {
		return null;
	}

	return { points };
}

export function parseFenceArea(
	area: string,
	type: FenceType
): CircleData | PolygonData | null {
	if (type === 1) {
		return parseCircleFromWKT(area);
	}
	return parsePolygonFromWKT(area);
}

export function circleToWKT(circle: CircleData): string {
	const [lat, lng] = circle.center;
	return `CIRCLE(${lat} ${lng}, ${circle.radius})`;
}

export function polygonToWKT(points: [number, number][]): string {
	if (points.length < 3) {
		return "";
	}
	const coordsStr = points.map(([lat, lng]) => `${lat} ${lng}`).join(", ");
	return `POLYGON((${coordsStr}))`;
}

export function isPolygonClosed(points: [number, number][]): boolean {
	if (points.length < 4) {
		return false;
	}
	const first = points[0];
	const last = points.at(-1);
	if (!last) {
		return false;
	}
	return first[0] === last[0] && first[1] === last[1];
}

export function closePolygon(points: [number, number][]): [number, number][] {
	if (points.length < 3) {
		return points;
	}
	return [...points, points[0]];
}

export function calculateDistance(
	point1: [number, number],
	point2: [number, number]
): number {
	const [lat1, lng1] = point1;
	const [lat2, lng2] = point2;
	const R = 6371e3;
	const φ1 = (lat1 * Math.PI) / 180;
	const φ2 = (lat2 * Math.PI) / 180;
	const Δφ = ((lat2 - lat1) * Math.PI) / 180;
	const Δλ = ((lng2 - lng1) * Math.PI) / 180;

	const a =
		Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
		Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

	return R * c;
}

export function mapSaveError(err: unknown): string {
	const axiosError = err as {
		response?: { status?: number; data?: { message?: string } };
	};

	if (axiosError.response?.status === 409) {
		return "Já existe uma cerca com este nome.";
	}
	if (axiosError.response?.status === 400) {
		return axiosError.response?.data?.message || "Dados inválidos";
	}
	if (axiosError.response?.status === 404) {
		return "Cerca não encontrada";
	}
	return "Erro ao salvar cerca";
}
